/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { RootState } from '../store';
import { Scenarios } from '../constants/scenarios';
import {
  getActiveGroup,
  getGroupDetails,
  listGroups,
  setDefaultGroup,
  updateGroup,
  createGroupWithFields,
  setResetGroupDetails,
  deleteGroup, 
} from '../store/slices/account-entries';

interface GroupSelectorModalProps {
  onGroupActivated?: (fields: string[]) => void;
  currentGroupFields: string[];
  activeGroupCorrelative: number | null;
  fieldCaptionMap: Record<string, string>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
  scenario?: Scenarios;
}

export const GroupSelectorModal: React.FC<GroupSelectorModalProps> = ({
  onGroupActivated,
  currentGroupFields,
  activeGroupCorrelative,
  fieldCaptionMap,
  open,
  onOpenChange,
  showTrigger = true,
  scenario = Scenarios.LibroDiarioEscenario,
}) => {
  const dispatch = useDispatch();
  const { userGroups, groupDetailsFields } = useSelector(
    (state: RootState) => state.accountEntries.scenarios[scenario]
  );

  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const setOpenState = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };
  const [selectedCorrelative, setSelectedCorrelative] = useState<number | null>(null);
  const [details, setDetails] = useState<typeof groupDetailsFields>([]);
  const [localActiveCorrelative, setLocalActiveCorrelative] = useState<number | null>(null);
  const [realActiveGroupFields, setRealActiveGroupFields] = useState<string[]>([]);

  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');

  const [saveToDialogOpen, setSaveToDialogOpen] = useState(false);
  const [selectedGroupToSave, setSelectedGroupToSave] = useState<number | null>(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (selectedCorrelative !== null) {
      dispatch(setResetGroupDetails(scenario));
      getGroupDetails(selectedCorrelative, scenario);
    }
  }, [selectedCorrelative, dispatch]);

  useEffect(() => {
    setDetails(groupDetailsFields);
  }, [groupDetailsFields]);

  useEffect(() => {
    setLocalActiveCorrelative(activeGroupCorrelative);
  }, [activeGroupCorrelative]);

  useEffect(() => {
    if (isOpen && localActiveCorrelative !== null) {
      dispatch(setResetGroupDetails(scenario));
      getGroupDetails(localActiveCorrelative, scenario);
    }
  }, [isOpen, localActiveCorrelative, dispatch]);

  useEffect(() => {
    if (isOpen && selectedCorrelative === null) {
      setSelectedCorrelative(localActiveCorrelative);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCorrelative === localActiveCorrelative && groupDetailsFields.length > 0) {
      const fields = groupDetailsFields.map((d: any) => d.Campo);
      setRealActiveGroupFields(fields);
    }
  }, [groupDetailsFields, selectedCorrelative, localActiveCorrelative]);

  const hasChanges = useMemo(() => {
    if (localActiveCorrelative === null) return false;
    if (currentGroupFields.length !== realActiveGroupFields.length) return true;
    return currentGroupFields.some((f, idx) => f !== realActiveGroupFields[idx]);
  }, [currentGroupFields, realActiveGroupFields, localActiveCorrelative]);

  const buildJsonCampos = (fields: string[]): string => {
    return JSON.stringify(
      fields.map((field, index) => ({
        Posicion: index,
        Cabecera: fieldCaptionMap[field] || field,
        Campo: field,
      }))
    );
  };

  const handleOpen = () => {
    setOpenState(true);
    setSelectedCorrelative(localActiveCorrelative);
    if (localActiveCorrelative !== null) {
      dispatch(setResetGroupDetails(scenario));
      getGroupDetails(localActiveCorrelative, scenario);
    }
  };

  const handleClose = () => {
    setOpenState(false);
    setSelectedCorrelative(null);
    setDetails([]);
    setConfirmDeleteOpen(false);
    setSaveToDialogOpen(false);
  };

  const handleSelectGroup = (correlative: number) => {
    setSelectedCorrelative(correlative);
  };

  const handleActivate = async () => {
    if (selectedCorrelative === null) return;
    await setDefaultGroup(selectedCorrelative, scenario);
    getActiveGroup(scenario);
    setLocalActiveCorrelative(selectedCorrelative);
    if (onGroupActivated) {
      const fields = details.map((d: any) => d.Campo);
      onGroupActivated(fields);
    }
    handleClose();
  };

  const handleRemoveGroup = async () => {
    if (onGroupActivated) {
      onGroupActivated([]);
    }
    setLocalActiveCorrelative(null);
    await setDefaultGroup(null, scenario);
    getActiveGroup(scenario);
    handleClose();
  };

  const handleSave = () => {
    setSelectedGroupToSave(localActiveCorrelative);
    setSaveToDialogOpen(true);
  };

  const handleConfirmSaveTo = async () => {
    if (selectedGroupToSave === null) return;
    const jsonFields = buildJsonCampos(currentGroupFields);
    await updateGroup({ correlative: selectedGroupToSave, jsonFields }, scenario);
    setRealActiveGroupFields(currentGroupFields);
    listGroups(scenario);
    setSaveToDialogOpen(false);
    handleClose();
  };

  const handleCancelSaveTo = () => {
    setSaveToDialogOpen(false);
    setSelectedGroupToSave(null);
  };

  const handleDeleteGroup = (correlative: number) => {
    setGroupToDelete(correlative);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (groupToDelete === null) return;
    await deleteGroup(groupToDelete, scenario);
    listGroups(scenario);
    setConfirmDeleteOpen(false);
    setGroupToDelete(null);
    if (groupToDelete === localActiveCorrelative) {
      setLocalActiveCorrelative(null);
      setRealActiveGroupFields([]);
    }
    setSelectedCorrelative(null);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setGroupToDelete(null);
  };

  const handleSaveAs = () => {
    setSaveAsDialogOpen(true);
    setSaveAsName('');
  };

  const handleConfirmSaveAs = async () => {
    if (!saveAsName.trim()) return;
    const jsonFields = buildJsonCampos(currentGroupFields);
    await createGroupWithFields({ name: saveAsName.trim(), jsonFields }, scenario);
    setSaveAsDialogOpen(false);
    listGroups(scenario);
    handleClose();
  };

  const handleCancelSaveAs = () => {
    setSaveAsDialogOpen(false);
    setSaveAsName('');
  };

  const isActiveGroup = (correlative: number) => {
    return localActiveCorrelative === correlative;
  };

  const activeGroupName = userGroups.find(g => g.Correlativo === localActiveCorrelative)?.Descripcion || '';

  const showSaveAs = currentGroupFields.length > 0 && (localActiveCorrelative === null || hasChanges);

  const isPreviewingSelected = selectedCorrelative !== null && selectedCorrelative !== localActiveCorrelative;
  const previewFields = isPreviewingSelected ? details.map((d: any) => d.Campo) : currentGroupFields;
  const previewCaption = isPreviewingSelected ? 'Vista previa del grupo seleccionado' : 'Agrupación actual';

  const removedFields = useMemo(() => {
    if (localActiveCorrelative === null) return [];
    return realActiveGroupFields.filter(f => !currentGroupFields.includes(f));
  }, [realActiveGroupFields, currentGroupFields, localActiveCorrelative]);

  return (
    <>
      {showTrigger && (
        <Tooltip title="Seleccionar agrupamiento">
          <IconButton onClick={handleOpen} size="small" sx={{ color: '#1976d2' }}>
            <ViewAgendaIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '70vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Agrupamientos</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', p: 0 }}>
          <Box sx={{ width: '40%', borderRight: '1px solid #e0e0e0', overflowY: 'auto', p: 1, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Grupos</Typography>
              <Box>
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleRemoveGroup}
                  variant="outlined"
                  color="warning"
                  disabled={!localActiveCorrelative}
                  sx={{ mr: 1 }}
                >
                  Desactivar grupo
                </Button>
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    if (selectedCorrelative !== null) {
                      handleDeleteGroup(selectedCorrelative);
                    }
                  }}
                  variant="outlined"
                  color="error"
                  disabled={selectedCorrelative === null}
                >
                  Eliminar
                </Button>
              </Box>
            </Box>
            <List dense>
              {userGroups && userGroups.length > 0 ? (
                userGroups.map((group: any) => (
                  <ListItem
                    key={group.Correlativo}
                    button
                    selected={selectedCorrelative === group.Correlativo}
                    onClick={() => handleSelectGroup(group.Correlativo)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      backgroundColor: isActiveGroup(group.Correlativo)
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'transparent',
                      borderLeft: isActiveGroup(group.Correlativo)
                        ? '3px solid #1976d2'
                        : '3px solid transparent',
                    }}
                  >
                    <ListItemText
                      primary={`${group.Correlativo} - ${group.Descripcion}`}
                      secondary={
                        <>
                          {isActiveGroup(group.Correlativo) && (
                            <Chip label="Activo" size="small" color="primary" sx={{ mr: 0.5 }} />
                          )}
                        </>
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
                  No hay grupos disponibles
                </Typography>
              )}
            </List>
          </Box>

          <Box sx={{ width: '60%', overflowY: 'auto', p: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              {previewCaption}
              {localActiveCorrelative !== null && !isPreviewingSelected && (
                <Chip
                  label={`Grupo activo: ${activeGroupName}`}
                  size="small"
                  color="info"
                  sx={{ ml: 1 }}
                />
              )}
              {hasChanges && !isPreviewingSelected && (
                <Chip label="Modificada" size="small" color="warning" sx={{ ml: 1 }} />
              )}
            </Typography>

            {isPreviewingSelected ? (
              previewFields.length > 0 ? (
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {previewFields.map((field, idx) => (
                    <Box
                      key={field}
                      component="li"
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 0.5,
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <Typography variant="body2">
                        {details.find((d: any) => d.Campo === field)?.Cabecera || field}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Orden {idx + 1}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Este grupo no tiene campos definidos.
                </Typography>
              )
            ) : (
              <>
                {currentGroupFields.length > 0 ? (
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    {currentGroupFields.map((field, idx) => {
                      const isNew = localActiveCorrelative !== null && !realActiveGroupFields.includes(field);
                      const isMoved = localActiveCorrelative !== null && realActiveGroupFields.includes(field) && realActiveGroupFields.indexOf(field) !== idx;
                      return (
                        <Box
                          key={field}
                          component="li"
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 0.5,
                            borderBottom: '1px solid #f0f0f0',
                            color: isNew ? 'primary.main' : isMoved ? 'warning.main' : 'inherit',
                          }}
                        >
                          <Typography variant="body2">
                            {fieldCaptionMap[field] || field}
                            {isNew && <Chip label="Nuevo" size="small" color="primary" sx={{ ml: 1 }} />}
                            {isMoved && <Chip label="Movido" size="small" color="warning" sx={{ ml: 1 }} />}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Orden {idx + 1}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No hay campos agrupados actualmente.
                  </Typography>
                )}

                {removedFields.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Campos eliminados:
                    </Typography>
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                      {removedFields.map((field) => (
                        <Box
                          key={field}
                          component="li"
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 0.5,
                            borderBottom: '1px solid #f0f0f0',
                            color: 'error.main',
                            textDecoration: 'line-through',
                          }}
                        >
                          <Typography variant="body2">
                            {fieldCaptionMap[field] || field}
                          </Typography>
                          <Typography variant="caption" color="error">
                            Eliminado
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {localActiveCorrelative !== null && hasChanges && (
                    <Button variant="contained" color="primary" onClick={handleSave}>
                      Guardar
                    </Button>
                  )}
                  {showSaveAs && (
                    <Button variant="outlined" onClick={handleSaveAs}>
                      Guardar como
                    </Button>
                  )}
                </Box>
              </>
            )}

            {selectedCorrelative !== null && selectedCorrelative !== localActiveCorrelative && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleActivate}
                  disabled={details.length === 0}
                >
                  Activar grupo
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saveToDialogOpen} onClose={handleCancelSaveTo} maxWidth="xs" fullWidth>
        <DialogTitle>Guardar agrupación en grupo</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel id="select-group-label">Seleccionar grupo</InputLabel>
            <Select
              labelId="select-group-label"
              value={selectedGroupToSave ?? ''}
              onChange={(e) => setSelectedGroupToSave(e.target.value as number)}
              label="Seleccionar grupo"
            >
              {userGroups.map((group: any) => (
                <MenuItem key={group.Correlativo} value={group.Correlativo}>
                  {group.Correlativo} - {group.Descripcion}
                  {isActiveGroup(group.Correlativo) && (
                    <Chip label="Activo" size="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            La agrupación actual se guardará en el grupo seleccionado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSaveTo}>Cancelar</Button>
          <Button
            onClick={handleConfirmSaveTo}
            variant="contained"
            disabled={selectedGroupToSave === null}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar el grupo seleccionado?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saveAsDialogOpen} onClose={handleCancelSaveAs} maxWidth="xs" fullWidth>
        <DialogTitle>Guardar como nuevo agrupamiento</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre del nuevo grupo"
            fullWidth
            value={saveAsName}
            onChange={(e) => setSaveAsName(e.target.value)}
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSaveAs}>Cancelar</Button>
          <Button onClick={handleConfirmSaveAs} variant="contained" disabled={!saveAsName.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};